import { FlatRentAmendments } from '@features/amendments/components/FlatRentAmendments'
import { ContractClausesEditor } from '@features/contracts/components/ContractClausesEditor'
import { ContractDraftHistory } from '@features/contracts/components/ContractDraftHistory'
import { ContractTable } from '@features/contracts/components/ContractTable'
import { useContractPreview } from '@services/contracts/mutations/useContractPreview'
import { useCreateContract } from '@services/contracts/mutations/useCreateContract'
import { useSendFlatContractDraft } from '@services/contracts/mutations/useSendFlatContractDraft'
import { useContracts } from '@services/contracts/queires/useContracts'
import type { ApiError } from '@services/utils/ApiError'
import { Eye, FilePen, FileText, Plus, Send } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FlatDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

import { AttachmentSection } from './AttachmentSection'
import { FlatProtocols } from './FlatProtocols'

// Contract rows still waiting for signatures (see ResendContract in OfferActions).
const RESENDABLE_STATUSES = ['awaiting', 'sent', 'opened']

type IncompleteProfile = {
  users: Array<{ email: string; role: string; errors: Record<string, string> }>
  flat: Record<string, string>
}

function SectionCard({
  title,
  icon: Icon,
  children
}: {
  title: string
  icon: typeof FileText
  children: ReactNode
}) {
  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="m-0 font-display text-base font-bold tracking-tight text-ink">
          {title}
        </h3>
      </div>
      {children}
    </Card>
  )
}

export function FlatsContracts({ flat }: { flat: FlatDetail }) {
  const { t } = useTranslation('flat')
  const { t: tr } = useTranslation('offers')
  const { t: tb } = useTranslation('backend')
  const { confirm, dialog } = useConfirm()

  const {
    mutate: createContract,
    isPending: creating,
    error: createError
  } = useCreateContract()
  const { mutate: previewNewContract, error: previewError } =
    useContractPreview()
  const { data, isLoading } = useContracts({
    params: { type: 'flat', flat: flat.id }
  })

  const [paymentTime, setPaymentTime] = useState('END_CURRENT_MONTH')
  const [depositPaid, setDepositPaid] = useState(false)
  const [incomplete, setIncomplete] = useState<IncompleteProfile | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const { t: tClauses } = useTranslation('clauses')

  // Surface "incomplete profile" validation errors from create/preview.
  useEffect(() => {
    const error = (createError || previewError) as ApiError | null
    if (!error) return
    if (
      error.message === 'backend.offer_user_incomplete_profile_error' &&
      error.extra
    ) {
      const v = error.extra as Partial<IncompleteProfile>
      setIncomplete({ users: v.users || [], flat: v.flat || {} })
    }
  }, [createError, previewError])

  const contracts = data?.results ?? []
  const hasContracts = contracts.length > 0
  const fullySigned =
    contracts[0]?.signature_count === contracts[0]?.signed_count
  // Nobody signed yet: the points can still change, as a new version.
  const resendable =
    hasContracts &&
    contracts.every((c) => RESENDABLE_STATUSES.includes(c.status ?? ''))

  const handleCreate = async () => {
    if (!flat.id) return
    if (
      await confirm({
        title: tr('admin.actions.confirm_create_contract'),
        confirmText: tr('admin.form.yes'),
        cancelText: tr('admin.form.no')
      })
    ) {
      createContract({
        body: { id: flat.id, payment_time: paymentTime, depositPaid }
      })
    }
  }

  const { isPending: isSendingDraft, mutate: sendDraft } =
    useSendFlatContractDraft()

  const handleSendDraft = async () => {
    if (!flat.id) return
    // E-mail majiteľom sa nedá vziať späť — preto potvrdenie.
    if (
      await confirm({
        title: t('contract.confirm_send_draft'),
        description: t('contract.send_draft_description'),
        confirmText: tr('admin.form.yes'),
        cancelText: tr('admin.form.no')
      })
    ) {
      sendDraft({
        flat_id: flat.id,
        payment_time: paymentTime,
        deposit_paid: depositPaid
      })
    }
  }

  const handlePreview = () =>
    previewNewContract({
      id: flat.id,
      params: { payment_time: paymentTime, deposit_paid: depositPaid }
    })

  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-2xl bg-ink/[0.04]" />
  }

  const attachmentsByCategory = (category: 'AMENDMENT' | 'OTHER') =>
    contracts[0]?.attachments?.reduce(
      (acc: Record<string, unknown[]>, attachment) => {
        if (attachment.category === category) {
          acc.items = acc.items || []
          acc.items.push(attachment)
        }
        return acc
      },
      {}
    ) as never

  return (
    <div className="space-y-6">
      {dialog}

      <SectionCard title={t('contract.card_title')} icon={FileText}>
        {hasContracts ? (
          <div className="space-y-4">
            <ContractTable flatId={flat.id} type="flat" />
            {resendable && contracts[0]?.id && (
              <>
                <Button
                  type="button"
                  variant="ghostDark"
                  onClick={() => setEditorOpen(true)}
                >
                  <FilePen className="h-4 w-4" />
                  {tClauses('open_editor_resend')}
                </Button>
                <ContractClausesEditor
                  open={editorOpen}
                  onOpenChange={setEditorOpen}
                  target={{
                    kind: 'flat',
                    flatId: flat.id!,
                    // Flat contract rows carry payment_time; the list type is the tenant one.
                    paymentTime:
                      (contracts[0] as { payment_time?: string })
                        .payment_time ?? 'END_CURRENT_MONTH'
                  }}
                  resend={{ contractId: contracts[0].id, type: 'flat' }}
                />
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-1.5 block">
                  {t('contract.payment_time')}
                </Label>
                <Combobox
                  searchable={false}
                  value={paymentTime}
                  onChange={(v) => setPaymentTime(v ?? 'END_CURRENT_MONTH')}
                  options={[
                    {
                      value: 'END_CURRENT_MONTH',
                      label: t('contract.end_current_month')
                    },
                    {
                      value: 'HALF_NEXT_MONTH',
                      label: t('contract.half_next_month')
                    },
                    {
                      value: 'END_NEXT_MONTH',
                      label: t('contract.end_next_month')
                    }
                  ]}
                />
              </div>
              <label className="flex cursor-pointer items-end gap-3 pb-2.5">
                <Switch
                  checked={depositPaid}
                  onCheckedChange={setDepositPaid}
                />
                <span className="text-sm font-medium text-ink">
                  {t('contract.deposit_paid')}
                </span>
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={handleCreate} disabled={creating}>
                <Plus className="h-4 w-4" />
                {t('contract.create_contract')}
              </Button>
              <Button type="button" variant="ghostDark" onClick={handlePreview}>
                <Eye className="h-4 w-4" />
                {t('contract.view_contract')}
              </Button>
              <Button
                type="button"
                variant="ghostDark"
                onClick={handleSendDraft}
                disabled={isSendingDraft}
              >
                <Send className="h-4 w-4" />
                {t('contract.send_draft')}
              </Button>
              <Button
                type="button"
                variant="ghostDark"
                onClick={() => setEditorOpen(true)}
              >
                <FilePen className="h-4 w-4" />
                {tClauses('open_editor')}
              </Button>
            </div>
            {flat.id && (
              <ContractDraftHistory
                target={{ kind: 'flat', flatId: flat.id }}
              />
            )}
            {flat.id && (
              <ContractClausesEditor
                open={editorOpen}
                onOpenChange={setEditorOpen}
                target={{
                  kind: 'flat',
                  flatId: flat.id,
                  paymentTime,
                  depositPaid
                }}
              />
            )}

            {incomplete && (
              <div className="space-y-4 rounded-2xl border border-destructive/20 bg-destructive/[0.04] p-4">
                {incomplete.users.map((user, index) => (
                  <div key={`${user.email}-${index}`} className="space-y-2">
                    <p className="m-0 text-sm font-semibold text-destructive">
                      {tb(`backend.${user.role}.escape`, { email: user.email })}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(user.errors).map(([key, value]) => (
                        <span
                          key={key}
                          className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive"
                        >
                          {tb(value)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                {Object.keys(incomplete.flat).length > 0 && (
                  <div className="space-y-2">
                    <p className="m-0 text-sm font-semibold text-destructive">
                      {t('contract.flat_missing_fields')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(incomplete.flat).map(([key, value]) => (
                        <span
                          key={key}
                          className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive"
                        >
                          {tb(value)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <FlatProtocols flatId={flat.id} />

      {hasContracts && fullySigned && (
        <>
          <FlatRentAmendments flat={flat} />
          <AttachmentSection
            attachments={attachmentsByCategory('AMENDMENT')}
            title={t('contract.amendments')}
            contract_id={contracts[0]?.id}
            category="AMENDMENT"
            type="flat"
          />
          <AttachmentSection
            attachments={attachmentsByCategory('OTHER')}
            title={t('contract.other')}
            contract_id={contracts[0]?.id}
            category="OTHER"
            type="flat"
          />
        </>
      )}
    </div>
  )
}
